import * as ipaddr from 'ipaddr.js';

const IPV4_BITS = 32;
const IPV6_BITS = 128;

export const DEFAULT_MAX_CIDRS_PER_IP_RANGE = 64;

export type IpRangeToCidrsResult = {
  cidrs: string[];
  truncated: boolean;
};

const toBigInt = (bytes: number[]): bigint =>
  bytes.reduce((accumulator, byte) => (accumulator << 8n) + BigInt(byte), 0n);

const toAddressString = ({
  value,
  bits,
}: {
  value: bigint;
  bits: number;
}): string => {
  const byteCount = bits / 8;
  const bytes = Array.from({ length: byteCount }, (_, index) =>
    Number((value >> BigInt((byteCount - 1 - index) * 8)) & 0xffn),
  );
  return ipaddr.fromByteArray(bytes).toString();
};

const countTrailingZeroBits = ({
  value,
  bits,
}: {
  value: bigint;
  bits: number;
}): number => {
  if (value === 0n) {
    return bits;
  }

  let remaining = value;
  let count = 0;
  while ((remaining & 1n) === 0n) {
    remaining >>= 1n;
    count += 1;
  }
  return count;
};

const floorLog2 = (value: bigint): number => value.toString(2).length - 1;

const parseEndpoint = (value: string): ipaddr.IPv4 | ipaddr.IPv6 | null => {
  try {
    return ipaddr.parse(value.trim());
  } catch {
    return null;
  }
};

export const ipRangeToCidrs = ({
  firstIp,
  lastIp,
  maxCidrs = DEFAULT_MAX_CIDRS_PER_IP_RANGE,
}: {
  firstIp: string;
  lastIp: string;
  maxCidrs?: number;
}): IpRangeToCidrsResult => {
  const first = parseEndpoint(firstIp);
  const last = parseEndpoint(lastIp);

  if (!first || !last || first.kind() !== last.kind()) {
    return { cidrs: [], truncated: false };
  }

  const bits = first.kind() === 'ipv4' ? IPV4_BITS : IPV6_BITS;
  const end = toBigInt(last.toByteArray());
  let start = toBigInt(first.toByteArray());

  if (start > end) {
    return { cidrs: [], truncated: false };
  }

  const cidrs: string[] = [];
  while (start <= end) {
    if (cidrs.length >= maxCidrs) {
      return { cidrs, truncated: true };
    }

    const hostBits = Math.min(
      countTrailingZeroBits({ value: start, bits }),
      floorLog2(end - start + 1n),
    );
    cidrs.push(`${toAddressString({ value: start, bits })}/${bits - hostBits}`);
    start += 1n << BigInt(hostBits);
  }

  return { cidrs, truncated: false };
};
